/**
 * The Traces tab's Logs view: the log records the project's services sent over OpenTelemetry (vfs-service
 * /telemetry/logs), newest first, in the tab's time range. Found by text (message or attribute), filtered by service and
 * level; a record of a request (its trace id) opens that request in the tab (`onOpenTrace`). The first page refreshes
 * every 5 s like the map (not while paused, the page hidden or older records are shown); "Show older" pages back.
 * Lines a service only prints to its output are not here: they are in the Logs tab.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { listLogs } from '../../services/TelemetryService';
import { REFRESH_MS, plural } from './tracesModel';
import { LOGS_LIMIT, SEVERITIES, attributesLine, logTime, severityPill } from './signalsModel';

const errorText = (error) => error?.body?.error || error?.message || String(error || 'Something went wrong');

function useDebounced(value, ms) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

export function LogsEmpty({ filtered }) {
  if (filtered) { return <p className="traces-muted traces-list-empty">No log record matches.</p>; }
  return (
    <div className="traces-state" data-testid="logs-empty">
      <strong>No log records in this range</strong>
      <p>
        Services send their log records here through OpenTelemetry. Java services started by Flow do it on their own; in
        other stacks, use the OpenTelemetry SDK with its logs exporter (OTLP): Flow already tells every service where to send
        them. What a service only prints is in the Logs tab.
      </p>
    </div>
  );
}

LogsEmpty.propTypes = { filtered: PropTypes.bool };

export default function LogsView({ projectId, range, paused = false, onOpenTrace = null }) {
  const [find, setFind] = useState('');
  const [service, setService] = useState('');
  const [severity, setSeverity] = useState('');
  const [answer, setAnswer] = useState(null);
  const [older, setOlder] = useState([]);
  const [next, setNext] = useState(null);
  const [error, setError] = useState(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const q = useDebounced(find.trim(), 300);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const page = await listLogs(projectId, { since: range, service, severity, q, limit: LOGS_LIMIT });
      if (mine !== seq.current) { return; }
      setAnswer(page);
      setOlder([]);
      setNext(page?.next ?? null);
      setError(null);
    } catch (e) {
      if (mine === seq.current) { setError(e); }
    }
  }, [projectId, range, service, severity, q]);

  useEffect(() => { load(); }, [load]);

  // The first page refreshes like the map: every 5 s while someone looks and no older page is open.
  useEffect(() => {
    if (paused || older.length) { return undefined; }
    const timer = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) { return; }
      load();
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load, paused, older.length]);

  const showOlder = async () => {
    if (!next) { return; }
    setLoadingOlder(true);
    try {
      const page = await listLogs(projectId, { since: range, service, severity, q, limit: LOGS_LIMIT, cursor: next });
      setOlder((rows) => [...rows, ...(page?.logs || [])]);
      setNext(page?.next ?? null);
    } catch (e) {
      setError(e);
    } finally {
      setLoadingOlder(false);
    }
  };

  if (error && !answer) {
    return (
      <div className="traces-state is-bad" role="alert">
        <strong>Could not load the logs</strong>
        <p>{errorText(error)}</p>
        <button className="traces-btn" onClick={() => load()} type="button">Try again</button>
      </div>
    );
  }
  if (!answer) { return <div className="traces-state" role="status">Loading the logs…</div>; }
  if (answer.enabled === false) {
    return (
      <div className="traces-state">
        <strong>Tracing is off for this sandbox</strong>
        <p>{answer.reason || 'The services of this sandbox do not send logs.'}</p>
      </div>
    );
  }

  const rows = [...(answer.logs || []), ...older];
  const services = answer.services || [];
  const filtered = Boolean(q || service || severity);
  const now = Date.now();
  return (
    <section aria-labelledby="logs-title" className="traces-card traces-logs" data-testid="logs-view">
      <div className="traces-list-head">
        <h2 id="logs-title">Log records <span className="traces-muted-inline">· {plural(answer.total || 0, 'record')}</span></h2>
        <label className="traces-sr" htmlFor="logs-find">Find in the logs</label>
        <input
          className="traces-input"
          id="logs-find"
          onChange={(e) => setFind(e.target.value)}
          placeholder="Text in the message or an attribute…"
          type="search"
          value={find}
        />
        <label className="traces-sr" htmlFor="logs-service">Service</label>
        <select className="traces-btn" id="logs-service" onChange={(e) => setService(e.target.value)} value={service}>
          <option value="">All services</option>
          {services.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
          {service && !services.some((s) => s.name === service) && <option value={service}>{service}</option>}
        </select>
        <label className="traces-sr" htmlFor="logs-severity">Level</label>
        <select className="traces-btn" id="logs-severity" onChange={(e) => setSeverity(e.target.value)} value={severity}>
          {SEVERITIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>
      {error && <p className="traces-muted" role="status">Could not refresh: {errorText(error)}</p>}
      {!rows.length ? <LogsEmpty filtered={filtered} /> : (
        <ul aria-label="Log records found" className="traces-rows">
          {rows.map((r) => {
            const pill = severityPill(r.severity);
            const attrs = attributesLine(r.attributes);
            return (
              <li className="traces-log" data-testid={`logs-row-${r.id}`} key={r.id}>
                <time className="traces-log-time traces-mono" dateTime={new Date(r.time).toISOString()} title={new Date(r.time).toLocaleString()}>{logTime(r.time, now)}</time>
                <span className={`traces-pill${pill.tone ? ` is-${pill.tone}` : ''}`}>{pill.text}</span>
                <span className="traces-log-service">{r.service}</span>
                <span className="traces-log-body traces-mono">
                  {r.body}
                  {attrs && <span className="traces-log-attrs">{attrs}</span>}
                </span>
                {r.traceId && onOpenTrace ? (
                  <button className="traces-pill is-link" onClick={() => onOpenTrace(r.traceId)} title={`Open the request ${r.traceId}`} type="button">
                    Request
                  </button>
                ) : <span />}
              </li>
            );
          })}
        </ul>
      )}
      {next && (
        <button className="traces-btn traces-logs-more" disabled={loadingOlder} onClick={showOlder} type="button">
          {loadingOlder ? 'Loading…' : 'Show older'}
        </button>
      )}
      <p className="traces-footnote is-bordered">Records with a request open it in Traces. Kept like the traces, the oldest go first when a service logs a lot.</p>
    </section>
  );
}

LogsView.propTypes = {
  // (traceId) => void: opens that request in the Traces tab.
  onOpenTrace: PropTypes.func,
  paused: PropTypes.bool,
  projectId: PropTypes.string.isRequired,
  // The tab's time range ('15m', '1h', …: tracesModel RANGES).
  range: PropTypes.string.isRequired,
};
